"use client";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { EmptyStageIllustration } from "@/components/illustrations/empty-stage-illustration";

interface PipelineEmptyStateProps {
  onAddProfiles?: () => void;
  /** True when the table is empty because filters/search hid existing rows. */
  filtered?: boolean;
}

/**
 * Shown in place of the table/card list when the current view has no rows.
 *
 * A filter/search miss is a different empty than a stage with nobody in it:
 * "Add profiles" would imply the job is empty. The filter rail is still
 * visible, so this copy tells the recruiter to widen the query.
 *
 * Below `md` (the same breakpoint `PipelineTable`/`MobilePipelineCards`
 * switch on, just above) the job view flows with page content instead of
 * locking to a fixed viewport slice (ARBI-664), so the old `h-full` +
 * `justify-center` combo never had a bounded box to center within — the
 * illustration ended up crammed flush against the toolbar above it.
 * `px-page py-12` gives it the same comfortable breathing room as the
 * sequences list empty state (`features/sequences/components/list/empty-state.tsx`)
 * instead; `md:` restores the exact desktop box (`h-full` + `px-10`, no
 * vertical padding) unchanged.
 */
export function PipelineEmptyState({
  onAddProfiles,
  filtered = false,
}: PipelineEmptyStateProps) {
  return (
    <div
      data-testid="pipeline-empty-state"
      className="flex flex-1 flex-col items-center justify-center bg-surface-primary px-page py-12 md:h-full md:px-10 md:py-0"
    >
      <div className="flex flex-col items-center gap-4">
        <EmptyStageIllustration draggable={false} />

        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-[7px] text-center">
            <h3 className="text-xl font-medium text-heading tracking-[-0.2px]">
              {filtered ? "No matching profiles" : "No profiles in this stage"}
            </h3>
            <p className="text-sm text-contrast/60 tracking-[-0.14px]">
              {filtered
                ? "Nothing matches the current filters. Clear or change them to see more candidates."
                : "No profiles yet! Start sourcing for some or upload them."}
            </p>
          </div>

          {!filtered && (
            <Button
              type="button"
              onClick={onAddProfiles}
              variant="secondary"
              size="sm"
              className="bg-contrast text-background hover:bg-contrast/90"
            >
              Add profiles
              <Icon name="IconPlusSmall" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
